import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './cn';

/** Ant Design `Pagination` replacement — compact pager with optional page-size changer. */
export function Pagination({
  current = 1, pageSize = 10, total = 0, onChange, showSizeChanger, pageSizeOptions = [10, 20, 50, 100],
  showTotal, size, className, disabled,
}: {
  current?: number;
  pageSize?: number;
  total?: number;
  onChange?: (page: number, pageSize: number) => void;
  showSizeChanger?: boolean;
  pageSizeOptions?: (number | string)[];
  showTotal?: (total: number, range: [number, number]) => React.ReactNode;
  size?: 'small' | 'default';
  className?: string;
  disabled?: boolean;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const cur = Math.min(Math.max(1, current), pages);
  const go = (p: number) => { const np = Math.min(Math.max(1, p), pages); if (np !== cur) onChange?.(np, pageSize); };

  // page-number window: first, last, cur-1..cur+1, with … gaps
  const win = new Set<number>([1, pages, cur, cur - 1, cur + 1]);
  const nums = [...win].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  const items: (number | '…')[] = [];
  nums.forEach((n, i) => { if (i > 0 && n - nums[i - 1] > 1) items.push('…'); items.push(n); });

  const sm = size === 'small';
  const btn = cn('inline-grid place-items-center rounded-control border border-line bg-surface font-medium text-content transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-line disabled:hover:bg-surface disabled:hover:text-content dark:hover:bg-brand-900/20', sm ? 'h-7 min-w-7 px-1.5 text-[13px]' : 'h-8 min-w-8 px-2 text-sm');
  const from = total === 0 ? 0 : (cur - 1) * pageSize + 1;
  const to = Math.min(cur * pageSize, total);

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {showTotal && <span className={cn('mr-1 text-muted', sm ? 'text-[13px]' : 'text-sm')}>{showTotal(total, [from, to])}</span>}
      <button type="button" aria-label="Previous page" disabled={disabled || cur <= 1} onClick={() => go(cur - 1)} className={btn}>
        <ChevronLeft className="size-4" />
      </button>
      {items.map((it, i) =>
        it === '…' ? (
          <span key={`e${i}`} className="px-1 text-subtle">…</span>
        ) : (
          <button key={it} type="button" aria-current={it === cur} disabled={disabled} onClick={() => go(it)}
            className={cn(btn, it === cur && 'border-brand-500! bg-brand-50! text-brand-700! dark:bg-brand-900/30!')}>
            {it}
          </button>
        ),
      )}
      <button type="button" aria-label="Next page" disabled={disabled || cur >= pages} onClick={() => go(cur + 1)} className={btn}>
        <ChevronRight className="size-4" />
      </button>
      {showSizeChanger && (
        <select
          value={pageSize}
          disabled={disabled}
          onChange={(e) => onChange?.(1, Number(e.target.value))}
          aria-label="Page size"
          className={cn('rounded-control border border-line bg-surface text-content outline-none transition-colors hover:border-brand-300 focus:border-brand-400', sm ? 'h-7 pl-2 pr-6 text-[13px]' : 'h-8 pl-2.5 pr-7 text-sm')}
        >
          {pageSizeOptions.map((n) => <option key={n} value={n}>{n} / page</option>)}
        </select>
      )}
    </div>
  );
}
